var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useCallback, useContext, useRef, useState } from 'react';
import AppContainer from 'containers/AppContainer';
import Header from 'components/Header';
import Content from 'components/Content';
import Button from 'components/Button';
import Text from 'components/Text';
import Footer from 'components/Footer';
import { LocaleContext } from 'containers/LocaleContext';
import Dropdown from 'components/Dropdown';
import Datepicker from 'components/Datepicker';
import Input from 'components/Input';
import { useGetCountries, useGetLocations } from '../queries/useLocationQueries';
import { useSearchReceipt } from '../queries/useReceiptQueries';
import { formatCurrency, getCurrencyISO3, random } from '../utils/functions';
import { useStore, useUpdateStore } from 'hooks/useStore';
import Loading from 'components/Loading';
import { useNavigate } from 'react-router';
export function EnterPaymentDetails() {
    const navigate = useNavigate();
    const { I18n } = useContext(LocaleContext);
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedCountry, setSelectedCountry] = useState('');
    const [selectedLocationId, setSelectedLocationId] = useState(0);
    const [selectedAmount, setSelectedAmount] = useState(0);
    const updateStore = useUpdateStore();
    const randomNumber = useRef(random(1, 2));
    const store = useStore();
    const country = useStore((s) => s.country);
    const locationId = useStore((s) => { var _a; return (_a = s.formData) === null || _a === void 0 ? void 0 : _a.locationId; });
    const date = useStore((s) => { var _a; return (_a = s.formData) === null || _a === void 0 ? void 0 : _a.date; });
    const amount = useStore((s) => { var _a; return (_a = s.formData) === null || _a === void 0 ? void 0 : _a.amount; });
    const { mutate: searchReceipt, isLoading: isSearchingReceipt } = useSearchReceipt();
    const { data: locationOptions, isLoading: isLoadingLocations } = useGetLocations({
        options: {
            select: (data) => data
                .filter((location) => location.country === selectedCountry)
                .sort((a, b) => { var _a; return (_a = a.name) === null || _a === void 0 ? void 0 : _a.localeCompare(b.name); })
                .map((location) => {
                return {
                    key: location.id.toString(),
                    text: `${location.name} (${location.city})`,
                    value: location.id.toString(),
                };
            }),
        },
    });
    const { data: countryOptions, isLoading: isLoadingCountries } = useGetCountries();
    const submit = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        const data = {
            date: selectedDate,
            amount: selectedAmount,
            language: I18n.locale,
            locationId: selectedLocationId,
        };
        // Saves data to storage to remember users selections if they return to home page
        updateStore((s) => {
            s.formData = Object.assign({ country: selectedCountry }, data);
        });
        // Makes the POST call to search for receipt if non found redirects to not found page
        searchReceipt(data, {
            onSuccess: (receiptDetails) => {
                store.setStore({
                    uid: receiptDetails.uid,
                });
                navigate(`success`);
            },
            onError: () => navigate('error'),
        });
    }), [
        selectedDate,
        selectedAmount,
        I18n,
        selectedLocationId,
        updateStore,
        searchReceipt,
        selectedCountry,
        store,
        navigate,
    ]);
    // Function to get random subtitle. When new subtitles are added increase the max value of Random number generator
    const getRandomSubtitle = () => I18n.t('receipts.sub_title_' + randomNumber.current.toString());
    return (React.createElement(AppContainer, null,
        React.createElement(Header, null),
        isSearchingReceipt ? (React.createElement(Loading, { state: "SEARCHING" })) : (React.createElement(React.Fragment, null,
            React.createElement(Content.Wrapper, null,
                React.createElement(Content.Base, { padded: true },
                    React.createElement(Content.Header, null,
                        React.createElement(Text.H1, { centered: true }, I18n.t('receipts.title')),
                        React.createElement(Text.H2, { centered: true }, getRandomSubtitle())),
                    React.createElement("div", { id: "receipt_form" },
                        React.createElement(Dropdown, { defaultValue: country, loading: isLoadingCountries || !country, type: "COUNTRY", label: I18n.t('receipts.form.session_country'), placeholder: I18n.t('receipts.form.select_fastned_station'), onChange: (option) => setSelectedCountry(option.value), options: countryOptions || [], "data-testid": "country-dropdown" }),
                        React.createElement(Dropdown, { loading: isLoadingLocations, type: "LOCATION", label: I18n.t('receipts.form.location'), placeholder: I18n.t('receipts.form.select_fastned_station'), onChange: (option) => setSelectedLocationId(parseInt(option.value, 10)), options: locationOptions || [], defaultValue: locationId, "data-testid": "location-dropdown" }),
                        React.createElement(Datepicker, { label: I18n.t('receipts.form.session_date'), onChange: (value) => {
                                setSelectedDate(value.toISOString());
                            }, defaultValue: date, "data-testid": "date-picker" }),
                        React.createElement(Input, { label: I18n.t('receipts.form.exact_amount'), textAsIcon: getCurrencyISO3(store.country), placeholder: formatCurrency({ locale: I18n.locale, amount: 0 }), type: "TEXT", onChange: (value) => setSelectedAmount(parseFloat(value.toString().replace(/,/, '.'))), defaultValue: amount, "data-testid": "amount-input" })),
                    React.createElement("div", { className: "info_text" },
                        React.createElement(Text.Base, { centered: true }, I18n.t('receipts.info_text'))))),
            React.createElement(Footer, null,
                React.createElement(Button, { disabled: !selectedDate || !selectedCountry || !selectedLocationId || !selectedAmount, onClick: submit }, I18n.t('buttons.search_receipt')))))));
}
